import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DateRange {
  startDate: string;
  endDate: string;
}

export const useDateRange = () => {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [selectedRange, setSelectedRange] = useState<string | null>(null);
  const [tempDates, setTempDates] = useState<DateRange>({
    startDate: '',
    endDate: ''
  });

  const dateRanges = [
    { label: t('dates.selectors_today'), value: 'today' },
    { label: t('dates.selectors_this_week'), value: 'thisWeek' },
    { label: t('dates.selectors_this_month'), value: 'thisMonth' },
    { label: t('dates.selectors_last_month'), value: 'lastMonth' },
    { label: t('dates.selectors_last_year'), value: 'thisYear' },
  ];

  const handleDateChange = (type: 'start' | 'end') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setTempDates(prev => ({
      ...prev,
      [type === 'start' ? 'startDate' : 'endDate']: newDate
    }));
  };

  const handleDateRangeSelect = (range: string) => {
    setSelectedRange(range);
    let startDate: string;
    let endDate: string;

    switch (range) {
      case 'today':
        startDate = endDate = new Date().toISOString().split('T')[0];
        break;
      case 'thisWeek':
        const startOfWeek = new Date();
        startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
        const endOfWeek = new Date();
        endOfWeek.setDate(endOfWeek.getDate() + (6 - endOfWeek.getDay()));
        startDate = startOfWeek.toISOString().split('T')[0];
        endDate = endOfWeek.toISOString().split('T')[0];
        break;
      case 'thisMonth':
        const startOfMonth = new Date();
        startOfMonth.setDate(1);
        const endOfMonth = new Date();
        endOfMonth.setMonth(endOfMonth.getMonth() + 1);
        endOfMonth.setDate(0);
        startDate = startOfMonth.toISOString().split('T')[0];
        endDate = endOfMonth.toISOString().split('T')[0];
        break;
      case 'lastMonth':
        const startOfLastMonth = new Date();
        startOfLastMonth.setMonth(startOfLastMonth.getMonth() - 1);
        startOfLastMonth.setDate(1);
        const endOfLastMonth = new Date();
        endOfLastMonth.setMonth(endOfLastMonth.getMonth(), 0);
        startDate = startOfLastMonth.toISOString().split('T')[0];
        endDate = endOfLastMonth.toISOString().split('T')[0];
        break;
      case 'thisYear':
        const startOfLastYear = new Date();
        startOfLastYear.setFullYear(startOfLastYear.getFullYear());
        startOfLastYear.setMonth(0);
        startOfLastYear.setDate(1);
        const endOfLastYear = new Date();
        endOfLastYear.setFullYear(endOfLastYear.getFullYear(), 11, 31);
        startDate = startOfLastYear.toISOString().split('T')[0];
        endDate = endOfLastYear.toISOString().split('T')[0];
        break;
      default:
        return;
    }

    setTempDates({ startDate, endDate });
  };

  const resetDates = () => {
    setTempDates({
      startDate: '',
      endDate: ''
    });
    setSelectedRange(null);
  };

  const saveDateChanges = () => {
    try {
      if (new Date(tempDates.startDate) > new Date(tempDates.endDate)) {
        return;
      }
      setStartDate(tempDates.startDate);
      setEndDate(tempDates.endDate);
      return true;
    } catch (error) {
      return false;
    }
  };

  const closeModal = () => {
    setTempDates({
      startDate,
      endDate
    });
  };

  return {
    startDate,
    endDate,
    selectedRange,
    tempDates,
    dateRanges,
    handleDateChange,
    handleDateRangeSelect,
    resetDates,
    saveDateChanges,
    closeModal,
    setTempDates
  };
}; 